import React, { useState } from 'react';
import { Mail, Lock, Globe, Check, Loader2 } from 'lucide-react';
import { CnceLogo } from '../components/icons/CnceLogo';
import { PrivacyDisclaimerModal } from '../components/modals/PrivacyDisclaimerModal';
type Language = 'it' | 'de' | 'en';
interface LanguageOption {
  code: Language;
  name: string;
  flag: string;
}
const languages: LanguageOption[] = [{
  code: 'it',
  name: 'Italiano',
  flag: '🇮🇹'
}, {
  code: 'de',
  name: 'Deutsch',
  flag: '🇩🇪'
}, {
  code: 'en',
  name: 'English',
  flag: '🇬🇧'
}];
const translations = {
  it: {
    title: 'Accedi',
    subtitle: 'Benvenuto in Esonero Digitale',
    email: 'Email',
    password: 'Password',
    remember: 'Ricordami',
    login: 'Accedi',
    emailPlaceholder: 'nome@esempio.it',
    passwordPlaceholder: 'Inserisci la tua password',
    loading: 'Accesso in corso...'
  },
  de: {
    title: 'Anmelden',
    subtitle: 'Willkommen bei Esonero Digitale',
    email: 'E-Mail',
    password: 'Passwort',
    remember: 'Angemeldet bleiben',
    login: 'Anmelden',
    emailPlaceholder: 'name@beispiel.de',
    passwordPlaceholder: 'Geben Sie Ihr Passwort ein',
    loading: 'Anmeldung läuft...'
  },
  en: {
    title: 'Sign In',
    subtitle: 'Welcome to Esonero Digitale',
    email: 'Email',
    password: 'Password',
    remember: 'Remember me',
    login: 'Sign In',
    emailPlaceholder: 'name@example.com',
    passwordPlaceholder: 'Enter your password',
    loading: 'Signing in...'
  }
};
interface LoginPageProps {
  onLogin: () => void;
}
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('it');
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const currentLanguage = languages.find(lang => lang.code === selectedLanguage);
  const t = translations[selectedLanguage];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Start loading
    setIsLoading(true);
    // Simulate AJAX call - show privacy modal after 1.5 seconds
    setTimeout(() => {
      setIsLoading(false);
      setShowPrivacyModal(true);
    }, 1500);
  };
  const handlePrivacyAccept = () => {
    setShowPrivacyModal(false);
    onLogin();
  };
  const handlePrivacyDecline = () => {
    setShowPrivacyModal(false);
    // Reset form or show message
  };
  return <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{
    backgroundColor: '#172637'
  }} data-id="element-446">
      {/* Login Card */}
      <div className="w-full max-w-md" data-id="element-447">
        {/* Logo and Title */}
        <div className="text-center mb-8" data-id="element-448">
          <div className="flex justify-center mb-4" data-id="element-449">
            <CnceLogo className="h-16 lg:h-20" data-id="element-450" />
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2" data-id="element-451">
            Esonero Digitale
          </h1>
          <p className="text-gray-300" data-id="element-452">{t.subtitle}</p>
        </div>

        {/* Login Form Card */}
        <div className="rounded-lg p-6 lg:p-8 border" style={{
        backgroundColor: '#1e3142',
        borderColor: '#2d4a63'
      }} data-id="element-453">
          <h2 className="text-xl font-semibold text-white mb-6" data-id="element-454">{t.title}</h2>

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-455">
            {/* Email Input */}
            <div data-id="element-456">
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-457">
                {t.email}
              </label>
              <div className="relative" data-id="element-458">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-459" />
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.emailPlaceholder} className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-800 text-white placeholder-gray-500" style={{
                backgroundColor: '#0f1f2e',
                borderColor: '#2d4a63'
              }} required disabled={isLoading} data-id="element-460" />
              </div>
            </div>

            {/* Password Input */}
            <div data-id="element-461">
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-462">
                {t.password}
              </label>
              <div className="relative" data-id="element-463">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-464" />
                <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t.passwordPlaceholder} className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-500" style={{
                backgroundColor: '#0f1f2e',
                borderColor: '#2d4a63'
              }} required disabled={isLoading} data-id="element-465" />
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center" data-id="element-466">
              <input id="remember" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="w-4 h-4 text-blue-600 border-gray-600 rounded focus:ring-blue-500 bg-gray-800" disabled={isLoading} data-id="element-467" />
              <label htmlFor="remember" className="ml-2 text-sm text-gray-300" data-id="element-468">
                {t.remember}
              </label>
            </div>

            {/* Login Button */}
            <button type="submit" disabled={isLoading} className="w-full py-3 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2" style={{
            backgroundColor: isLoading ? '#6B7280' : '#4a9eff'
          }} onMouseEnter={e => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = '#3b8eef';
            }
          }} onMouseLeave={e => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = '#4a9eff';
            }
          }} data-id="element-469">
              {isLoading ? <>
                  <Loader2 size={20} className="animate-spin" data-id="element-470" />
                  <span data-id="element-471">{t.loading}</span>
                </> : <span data-id="element-472">{t.login}</span>}
            </button>
          </form>
        </div>

        {/* Language Selector */}
        <div className="mt-6" data-id="element-473">
          <div className="relative" data-id="element-474">
            <button onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)} className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg transition-colors border" style={{
            backgroundColor: '#1e3142',
            borderColor: '#2d4a63'
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = '#2d4a63';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#1e3142';
          }} disabled={isLoading} data-id="element-475">
              <Globe size={20} className="text-gray-300" data-id="element-476" />
              <span className="font-medium text-gray-300" data-id="element-477">
                {currentLanguage?.flag} {currentLanguage?.name}
              </span>
            </button>

            {/* Language Dropdown */}
            {isLanguageMenuOpen && <div className="absolute bottom-full left-0 right-0 mb-2 rounded-lg overflow-hidden border" style={{
            backgroundColor: '#1e3142',
            borderColor: '#2d4a63'
          }} data-id="element-478">
                {languages.map(lang => <button key={lang.code} onClick={() => {
              setSelectedLanguage(lang.code);
              setIsLanguageMenuOpen(false);
            }} className="w-full flex items-center justify-between px-4 py-3 text-gray-300 transition-colors" onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#2d4a63';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }} data-id="element-479">
                    <span className="flex items-center gap-2" data-id="element-480">
                      <span data-id="element-481">{lang.flag}</span>
                      <span className="font-medium" data-id="element-482">{lang.name}</span>
                    </span>
                    {selectedLanguage === lang.code && <Check size={16} className="text-blue-400" data-id="element-483" />}
                  </button>)}
              </div>}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-400" data-id="element-484">
        <p data-id="element-485">© 2024 CNCE - Esonero Digitale</p>
      </div>

      {/* Privacy Disclaimer Modal */}
      <PrivacyDisclaimerModal isOpen={showPrivacyModal} onAccept={handlePrivacyAccept} onDecline={handlePrivacyDecline} language={selectedLanguage} data-id="element-486" />
    </div>;
}